import { useEffect, useState } from 'react';
import { Modal, Button, Group, Textarea, Text, Divider, Stack } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { FloorPlan } from '../editor/editor/objects/FloorPlan';
import { LoadAction } from '../editor/editor/actions/LoadAction';
import { SaveAction } from '../editor/editor/actions/SaveAction';

/**
 * Two ways to hand the plan to someone else: a file, or a block of text small
 * enough to paste into a message. Both carry the same JSON, so a plan can go
 * out one way and come back the other.
 */
export function ShareModal({ opened, onClose }: { opened: boolean, onClose: () => void }) {
    const [mine, setMine] = useState('');
    const [theirs, setTheirs] = useState('');

    useEffect(() => {
        if (opened) {
            setMine(FloorPlan.Instance.save());
            setTheirs('');
        }
    }, [opened]);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(mine);
            showNotification({ message: 'Plan copied. Paste it into a message.', color: 'teal' });
        } catch {
            showNotification({ title: 'Could not reach the clipboard', message: 'Select the text and copy it by hand.', color: 'red' });
        }
    };

    const receive = () => {
        const text = theirs.trim();
        if (!text) { return; }
        try {
            JSON.parse(text);
        } catch {
            showNotification({ title: 'That is not a plan', message: 'Paste the whole block, from the first brace to the last.', color: 'red' });
            return;
        }
        new LoadAction(text).execute();
        showNotification({ message: 'Loaded their plan. Your own is replaced, so save yours first if you want to keep it.', color: 'teal' });
        onClose();
    };

    return (
        <Modal opened={opened} onClose={onClose} title="Pass this plan to someone else" size="lg">
            <Stack spacing="xs">
                <Text size="sm">
                    Everything in the room, walls and furniture, is in the text below.
                    Whoever opens it sees exactly what you see, and can send it back
                    changed.
                </Text>
                <Textarea value={mine} readOnly autosize minRows={3} maxRows={6} styles={{ input: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 11 } }} />
                <Group>
                    <Button onClick={copy}>Copy to clipboard</Button>
                    <Button variant="light" onClick={() => new SaveAction().execute()}>Download as a file</Button>
                </Group>

                <Divider my="sm" label="or take one in" labelPosition="center" />

                <Textarea
                    value={theirs}
                    onChange={(e) => setTheirs(e.currentTarget.value)}
                    placeholder="Paste the plan they sent you here"
                    autosize
                    minRows={3}
                    maxRows={6}
                    styles={{ input: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 11 } }}
                />
                <Group position="right">
                    <Button variant="default" onClick={onClose}>Cancel</Button>
                    <Button onClick={receive} disabled={!theirs.trim()}>Open their plan</Button>
                </Group>
            </Stack>
        </Modal>
    );
}
